import { Component, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { UsuarioService } from '../../services/usuario.service';
import { ModalComponent } from '../modal/modal.component';

@Component({
  selector: 'app-emergencia',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent, NgxMaskDirective],
  providers: [provideNgxMask()],
  templateUrl: './emergencia.component.html',
  styleUrl: './emergencia.component.css'
})
export class EmergenciaComponent {
  @ViewChild(ModalComponent) mdl!: ModalComponent;

  private usuarioService = inject(UsuarioService);

  tabActiva: 'general' | 'vivienda' = 'general';
  cedula = '';
  resultado: any = null;

  // Tab vivienda
  cedulaVivienda = '';
  resultadoVivienda: any = null;
  cedulaViviendaValidada = false;
  afectacionVivienda = '';
  estatusViviendaPrincipal = '';

  fotosVivienda = [
    { archivo: null as File | null, nombre: '', descripcion: '', placeholder: 'Ej: Fachada principal' },
    { archivo: null as File | null, nombre: '', descripcion: '', placeholder: 'Ej: Daño en techo o pared' },
    { archivo: null as File | null, nombre: '', descripcion: '', placeholder: 'Ej: Interior o habitación afectada' }
  ];

  titulo = '';
  tipo_color = '';
  letra_color = '';
  mensaje = '';

  // Datos de emergencia (reporte general)
  desaparecido = '';
  ultimaUbicacion = '';
  fotografiaNombre = '';
  fotografiaArchivo: File | null = null;

  estadoSalud = '';
  estadoSaludOtro = '';

  estadoVivienda = '';

  opcionesDesaparecido = [
    { value: 'si', label: 'Sí' },
    { value: 'no', label: 'No' }
  ];

  opcionesEstadoSalud = [
    { value: 'ileso', label: 'Ileso / Buen estado de salud' },
    { value: 'enfermo', label: 'Enfermo (Con síntomas de malestar o patología previa/activa)' },
    { value: 'herido', label: 'Herido (Requiere atención médica inmediata o traslado)' },
    { value: 'fallecido', label: 'Fallecido' },
    { value: 'otro', label: 'Otro' }
  ];

  opcionesEstadoVivienda = [
    { value: 'sin_afectacion', label: 'Sin afectación: Estructura segura y habitable.' },
    { value: 'afectacion_parcial', label: 'Afectación parcial: Daños leves o moderados, pero la vivienda sigue siendo habitable.' },
    { value: 'inhabitable', label: 'Inhabitable: Daños estructurales graves / Alto riesgo (No apta para permanecer en ella).' },
    { value: 'perdida_total', label: 'Pérdida total: Estructura colapsada o destruida.' }
  ];

  opcionesAfectacionVivienda = [
    { value: 'leve', label: 'Leve (grietas superficiales en pintura o friso)' },
    { value: 'moderada', label: 'Moderada (grietas en paredes divisorias y caída de revestimientos)' },
    { value: 'fuerte', label: 'Fuerte (daños severos, grietas estructurales o inclinación)' },
    { value: 'destruccion', label: 'Destrucción / pérdida total (derrumbe)' }
  ];

  opcionesEstatusViviendaPrincipal = [
    { value: 'habitable', label: 'Habitable' },
    { value: 'afectada', label: 'Afectada' },
    { value: 'inhabitable', label: 'Inhabitable' },
    { value: 'perdida_total', label: 'Pérdida total' }
  ];

  private etiquetasEstadoVivienda: Record<string, string> = {
    sin_afectacion: 'Sin afectación',
    afectacion_parcial: 'Afectación parcial',
    inhabitable: 'Inhabitable',
    perdida_total: 'Pérdida total'
  };

  /** Opciones de estatus filtradas según la afectación seleccionada. */
  get opcionesEstatusFiltradas(): { value: string; label: string }[] {
    if (!this.afectacionVivienda) {
      return [];
    }
    if (this.afectacionVivienda === 'leve' || this.afectacionVivienda === 'moderada') {
      return this.opcionesEstatusViviendaPrincipal.filter(
        (op) => op.value === 'habitable' || op.value === 'afectada'
      );
    }
    if (this.afectacionVivienda === 'fuerte' || this.afectacionVivienda === 'destruccion') {
      return this.opcionesEstatusViviendaPrincipal.filter(
        (op) => op.value === 'inhabitable' || op.value === 'perdida_total'
      );
    }
    return [];
  }

  onAfectacionViviendaChange(): void {
    // Al cambiar la afectación, se limpia el estatus porque las opciones disponibles cambian.
    this.estatusViviendaPrincipal = '';
    this.reiniciarFotosVivienda();
  }

  get requiereFotosVivienda(): boolean {
    return (
      this.estatusViviendaPrincipal === 'inhabitable' ||
      this.estatusViviendaPrincipal === 'perdida_total'
    );
  }

  onEstatusViviendaChange(): void {
    if (!this.requiereFotosVivienda) {
      this.reiniciarFotosVivienda();
    }
  }

  private reiniciarFotosVivienda(): void {
    this.fotosVivienda = [
      { archivo: null, nombre: '', descripcion: '', placeholder: 'Ej: Fachada principal' },
      { archivo: null, nombre: '', descripcion: '', placeholder: 'Ej: Daño en techo o pared' },
      { archivo: null, nombre: '', descripcion: '', placeholder: 'Ej: Interior o habitación afectada' }
    ];
  }

  onFotoViviendaSeleccionada(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const archivo = input.files?.[0] ?? null;

    if (!archivo) {
      this.fotosVivienda[index].archivo = null;
      this.fotosVivienda[index].nombre = '';
      return;
    }

    const maxBytes = 5 * 1024 * 1024;
    const formatosPermitidos = ['image/jpeg', 'image/jpg', 'image/png'];

    if (!formatosPermitidos.includes(archivo.type)) {
      input.value = '';
      this.fotosVivienda[index].archivo = null;
      this.fotosVivienda[index].nombre = '';
      this.mostrarAlerta(
        '!ADVERTENCIA¡',
        'Solo se permiten imágenes en formato JPG, JPEG o PNG, con un tamaño máximo de 5 MB.',
        'warning',
        'dark'
      );
      return;
    }

    if (archivo.size > maxBytes) {
      input.value = '';
      this.fotosVivienda[index].archivo = null;
      this.fotosVivienda[index].nombre = '';
      this.mostrarAlerta(
        '!ADVERTENCIA¡',
        'La imagen supera el tamaño máximo permitido de 5 MB. Reduzca el tamaño o seleccione otra imagen (JPG, JPEG o PNG) de hasta 5 MB.',
        'warning',
        'dark'
      );
      return;
    }

    this.fotosVivienda[index].archivo = archivo;
    this.fotosVivienda[index].nombre = archivo.name;
  }

  seleccionarTab(tab: 'general' | 'vivienda'): void {
    this.tabActiva = tab;
  }

  private mostrarAlerta(
    titulo: string,
    mensaje: string,
    tipo_color: string = 'warning',
    letra_color: string = 'dark'
  ): void {
    this.titulo = titulo;
    this.mensaje = mensaje;
    this.tipo_color = tipo_color;
    this.letra_color = letra_color;
    this.mdl.alerta();
  }

  private reiniciarDatosEmergencia(): void {
    this.desaparecido = '';
    this.ultimaUbicacion = '';
    this.fotografiaNombre = '';
    this.fotografiaArchivo = null;
    this.estadoSalud = '';
    this.estadoSaludOtro = '';
    this.estadoVivienda = '';
  }

  private reiniciarDatosVivienda(): void {
    this.resultadoVivienda = null;
    this.cedulaViviendaValidada = false;
    this.afectacionVivienda = '';
    this.estatusViviendaPrincipal = '';
    this.reiniciarFotosVivienda();
  }

  limpiar(): void {
    this.resultado = null;
    this.reiniciarDatosEmergencia();
  }

  limpiarVivienda(): void {
    this.reiniciarDatosVivienda();
  }

  onDesaparecidoChange(): void {
    if (this.desaparecido !== 'si') {
      this.ultimaUbicacion = '';
      this.fotografiaNombre = '';
      this.fotografiaArchivo = null;
    }
  }

  onEstadoSaludChange(): void {
    if (this.estadoSalud !== 'otro') {
      this.estadoSaludOtro = '';
    }
  }

  onFotografiaSeleccionada(event: Event): void {
    const input = event.target as HTMLInputElement;
    const archivo = input.files?.[0] ?? null;

    if (!archivo) {
      this.fotografiaArchivo = null;
      this.fotografiaNombre = '';
      return;
    }

    const maxBytes = 5 * 1024 * 1024; // 5 MB
    const formatosPermitidos = ['image/jpeg', 'image/jpg', 'image/png'];
    const esFormatoValido = formatosPermitidos.includes(archivo.type);

    if (!esFormatoValido) {
      input.value = '';
      this.fotografiaArchivo = null;
      this.fotografiaNombre = '';
      this.mostrarAlerta(
        '!ADVERTENCIA¡',
        'Solo se permiten imágenes en formato JPG, JPEG o PNG, con un tamaño máximo de 5 MB.',
        'warning',
        'dark'
      );
      return;
    }

    if (archivo.size > maxBytes) {
      input.value = '';
      this.fotografiaArchivo = null;
      this.fotografiaNombre = '';
      this.mostrarAlerta(
        '!ADVERTENCIA¡',
        'La imagen supera el tamaño máximo permitido de 5 MB. Reduzca el tamaño o seleccione otra imagen (JPG, JPEG o PNG) de hasta 5 MB.',
        'warning',
        'dark'
      );
      return;
    }

    this.fotografiaArchivo = archivo;
    this.fotografiaNombre = archivo.name;
  }

  private validarReporte(): string | null {
    if (!this.resultado?.cedula) {
      return 'Debe consultar primero la cédula del aprendiz.';
    }
    if (!this.desaparecido) {
      return 'Debe indicar si el aprendiz está desaparecido o no localizado.';
    }
    if (this.desaparecido === 'si' && !this.ultimaUbicacion.trim()) {
      return 'Debe indicar la última ubicación conocida o estimada.';
    }
    if (!this.estadoSalud) {
      return 'Debe seleccionar el estado de salud del aprendiz.';
    }
    if (this.estadoSalud === 'otro' && !this.estadoSaludOtro.trim()) {
      return 'Debe especificar el estado de salud.';
    }
    if (!this.estadoVivienda) {
      return 'Debe seleccionar el estado de la vivienda del aprendiz.';
    }
    return null;
  }

  enviarReporte(): void {
    const errorValidacion = this.validarReporte();
    if (errorValidacion) {
      this.mostrarAlerta('!ADVERTENCIA¡', errorValidacion, 'warning', 'dark');
      return;
    }

    const formData = new FormData();
    formData.append('cedula', String(this.resultado.cedula));
    formData.append('desaparecido', this.desaparecido);
    formData.append('ultima_ubicacion', this.ultimaUbicacion.trim());
    formData.append('estado_salud', this.estadoSalud);
    formData.append('estado_salud_otro', this.estadoSaludOtro.trim());
    formData.append('estado_vivienda', this.estadoVivienda);

    if (this.desaparecido === 'si' && this.fotografiaArchivo) {
      formData.append('fotografia', this.fotografiaArchivo);
    }

    this.mdl.cargando();
    this.usuarioService.guardarDatosEmergencia(formData).subscribe({
      next: (rs) => {
        this.mdl.cerrar();
        this.mostrarAlerta(
          '!EXITO¡',
          rs?.message || 'Reporte de emergencia registrado exitosamente.',
          'success',
          'light'
        );
        this.limpiar();
      },
      error: (err) => {
        this.mdl.cerrar();
        const mensajeError = err?.message || 'No se pudo registrar el reporte de emergencia.';
        const esDuplicado = mensajeError.toLowerCase().includes('ya existe un reporte');
        this.mostrarAlerta(
          '!ADVERTENCIA¡',
          mensajeError,
          esDuplicado ? 'warning' : 'danger',
          esDuplicado ? 'dark' : 'light'
        );
        if (esDuplicado) {
          this.limpiar();
        }
      }
    });
  }

  consultar(): void {
    const valor = this.cedula.trim();
    if (!valor) {
      this.resultado = null;
      this.reiniciarDatosEmergencia();
      this.mostrarAlerta(
        '!ADVERTENCIA¡',
        'Ingrese una cédula para consultar.',
        'warning',
        'dark'
      );
      return;
    }

    this.resultado = null;
    this.reiniciarDatosEmergencia();
    this.mdl.cargando();

    this.usuarioService.consultarEmergencia(valor).subscribe({
      next: (rs) => {
        this.mdl.cerrar();
        const data = rs?.data ?? null;

        if (!data) {
          this.resultado = null;
          this.mostrarAlerta(
            '!ADVERTENCIA¡',
            rs?.message || 'No se encontró información para la cédula indicada.',
            'warning',
            'dark'
          );
          return;
        }

        if (data.reporte_registrado) {
          this.resultado = null;
          this.reiniciarDatosEmergencia();
          this.mostrarAlerta(
            '!ADVERTENCIA¡',
            'Ya existe un reporte de emergencia registrado para esta cédula. No se puede cargar otro reporte.',
            'warning',
            'dark'
          );
          return;
        }

        this.resultado = data;
      },
      error: (err) => {
        this.mdl.cerrar();
        this.resultado = null;
        this.mostrarAlerta(
          '!ERROR¡',
          err?.message || 'No se pudo consultar la cédula.',
          'danger',
          'light'
        );
      }
    });
  }

  consultarVivienda(): void {
    const valor = this.cedulaVivienda.trim();
    if (!valor) {
      this.reiniciarDatosVivienda();
      this.mostrarAlerta(
        '!ADVERTENCIA¡',
        'Ingrese una cédula para consultar.',
        'warning',
        'dark'
      );
      return;
    }

    this.reiniciarDatosVivienda();
    this.mdl.cargando();

    this.usuarioService.consultarDatosEmergencia(valor).subscribe({
      next: (rs) => {
        this.mdl.cerrar();
        const data = rs?.data ?? null;

        if (!data) {
          this.mostrarAlerta(
            '!ADVERTENCIA¡',
            'Esta cédula no tiene un reporte registrado en Reporte general. Debe registrarse primero en Reporte general para avanzar a Actualización vivienda principal.',
            'warning',
            'dark'
          );
          return;
        }

        this.resultadoVivienda = data;
        this.cedulaViviendaValidada = true;
      },
      error: (err) => {
        this.mdl.cerrar();
        this.reiniciarDatosVivienda();
        const mensajeError = err?.message
          || 'Esta cédula no tiene un reporte registrado en Reporte general. Debe registrarse primero en Reporte general para avanzar a Actualización vivienda principal.';
        this.mostrarAlerta(
          '!ADVERTENCIA¡',
          mensajeError,
          'warning',
          'dark'
        );
      }
    });
  }

  guardarActualizacionVivienda(): void {
    if (!this.cedulaViviendaValidada || !this.resultadoVivienda) {
      this.mostrarAlerta(
        '!ADVERTENCIA¡',
        'Debe consultar y validar la cédula antes de guardar.',
        'warning',
        'dark'
      );
      return;
    }
    if (!this.afectacionVivienda) {
      this.mostrarAlerta(
        '!ADVERTENCIA¡',
        'Debe indicar qué afectaciones recibió su vivienda principal.',
        'warning',
        'dark'
      );
      return;
    }
    if (!this.estatusViviendaPrincipal) {
      this.mostrarAlerta(
        '!ADVERTENCIA¡',
        'Debe seleccionar el estatus de vivienda principal.',
        'warning',
        'dark'
      );
      return;
    }

    if (this.requiereFotosVivienda) {
      for (let i = 0; i < this.fotosVivienda.length; i++) {
        const foto = this.fotosVivienda[i];
        if (!foto.archivo) {
          this.mostrarAlerta(
            '!ADVERTENCIA¡',
            `Debe cargar la Foto ${i + 1} de la vivienda.`,
            'warning',
            'dark'
          );
          return;
        }
        if (!foto.descripcion.trim()) {
          this.mostrarAlerta(
            '!ADVERTENCIA¡',
            `Debe indicar la descripción de la Foto ${i + 1}.`,
            'warning',
            'dark'
          );
          return;
        }
      }
    }

    const formData = new FormData();
    formData.append('cedula', String(this.resultadoVivienda.cedula || this.cedulaVivienda.trim()));
    formData.append('afectacion_vivienda', this.afectacionVivienda);
    formData.append('estatus_vivienda', this.estatusViviendaPrincipal);

    if (this.resultadoVivienda.id_datos_emergencia) {
      formData.append('id_datos_emergencia', String(this.resultadoVivienda.id_datos_emergencia));
    }

    if (this.requiereFotosVivienda) {
      this.fotosVivienda.forEach((foto, index) => {
        const n = index + 1;
        if (foto.archivo) {
          formData.append(`foto${n}`, foto.archivo);
        }
        formData.append(`foto${n}_descripcion`, foto.descripcion.trim());
      });
    }

    this.mdl.cargando();
    this.usuarioService.guardarActualizacionVivienda(formData).subscribe({
      next: (rs) => {
        this.mdl.cerrar();
        this.mostrarAlerta(
          '!EXITO¡',
          rs?.message || 'Actualización de vivienda registrada exitosamente.',
          'success',
          'light'
        );
        this.limpiarVivienda();
        this.cedulaVivienda = '';
      },
      error: (err) => {
        this.mdl.cerrar();
        const mensajeError = err?.message || 'No se pudo registrar la actualización de vivienda.';
        const esWarning =
          mensajeError.toLowerCase().includes('debe') ||
          mensajeError.toLowerCase().includes('no tiene') ||
          mensajeError.toLowerCase().includes('obligator');
        this.mostrarAlerta(
          esWarning ? '!ADVERTENCIA¡' : '!ERROR¡',
          mensajeError,
          esWarning ? 'warning' : 'danger',
          esWarning ? 'dark' : 'light'
        );
      }
    });
  }

  nombreCompleto(data: any): string {
    if (!data) {
      return '—';
    }
    const nombre = `${data.nombre || ''} ${data.apellido || ''}`.trim();
    return nombre || '—';
  }

  etiquetaEstadoVivienda(valor: string | null | undefined): string {
    if (!valor) {
      return '—';
    }
    return this.etiquetasEstadoVivienda[valor] || valor;
  }

  valorCampo(valor: unknown): string {
    if (valor === null || valor === undefined || valor === '') {
      return '—';
    }
    return String(valor);
  }
}
